import cx from 'classnames'
import React, { ReactElement, useRef } from 'react'
import * as styles from '../../styles/components/content-blocks/text.module.scss'
import reactElementToString from '../../utils/react-element-to-string'
import CrosshairLink from '../crosshair-link'
import TypeWriter from '../typewriter'
import Heading from '../heading'
import SmallTitle from '../small-title'
import ContentWrapper from '../content-wrapper'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { bezierEaseIn } from '../../utils/styles'
import { FillColor } from '../color-fill'
import { FeaturedLink } from '../../utils/types'

type ColorVariant = 'light' | 'dark'

export interface TextBlockProps {
    heading?: string
    title?: string
    featuredLink?: FeaturedLink
    backgroundType?: FillColor
    textJustification?: 'left' | 'center'
    textColorVariant?: ColorVariant
    role?: 'ingress' | 'paragraph'
    body?: ReactElement | string
}

const TextBlock = (props: TextBlockProps) => {
    const {
        heading,
        title,
        body,
        backgroundType = 'white',
        textJustification = 'left',
        featuredLink,
        textColorVariant = 'dark',
        role = 'paragraph',
    } = props

    const isLeftJustified = textJustification === 'left'
    const targetRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start end', 'start start'] })
    const opacity = useTransform(scrollYProgress, [0.12, 0.62], [0, 1], { ease: bezierEaseIn })
    const hasBeenInView = useInView(targetRef, { margin: '-50%', once: true })

    return (
        <>
            <ContentWrapper
                ref={targetRef}
                color={backgroundType}
                className={cx(styles.container)}
            >
                <div
                    className={cx(styles.contentContainer, {
                        [styles.contentLeftJustified]: isLeftJustified,
                    })}
                >
                    {heading &&
                        <Heading className={cx(styles.heading, styles[textColorVariant])}>
                            {heading}
                        </Heading>
                    }
                    {title &&
                        <SmallTitle className={cx(styles.title, styles[textColorVariant])} as="h3" aria-label={title}>
                            <TypeWriter text={title} enabled={hasBeenInView} />
                        </SmallTitle>
                    }
                    <motion.span style={{ opacity }} className={styles.bodyContainer} >
                        <span
                            className={cx(
                                styles.body,
                                isLeftJustified ? styles.textLeft : styles.textCenter,
                                styles[textColorVariant],
                                styles[role],
                            )}
                            dangerouslySetInnerHTML={{ __html: reactElementToString(body) }}
                        />
                        {featuredLink &&
                            <CrosshairLink
                                as={featuredLink.internalLink ? 'internalLink' : 'externalLink'}
                                href={featuredLink.href}
                                label={featuredLink.label}
                                className={styles.link}
                                colorVariant={textColorVariant}
                                openInNewTab={featuredLink.openInNewTab}
                            />
                        }
                    </motion.span>
                </div>
            </ContentWrapper>
        </>
    )
}

export default TextBlock
